import { Modal, Form, Select, Input, InputNumber, Button } from "antd";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { currencyAmountValidator } from "@/common/components/MultipleOfValidator";

interface Service {
    serviceCategoryItemId: number;
    serviceName: string;
    serviceCategoryItemName?: string;
    serviceCategoryId?: number;
    serviceCategoryName?: string;
    amount: number;
    coShare?: number | null;
    coveredAmount?: number | null;
    isAmountCoverage?: boolean | null;
    isServiceCovered?: boolean;
    selected?: any;
}

interface CategoryNode {
    title?: string;
    label?: string;
    value?: string;
    key?: string;
    children?: Array<{ title?: string; label?: string; value?: string; key?: string }>;
}

interface AddOtherServiceItemModalProps {
    visible: boolean;
    categories: CategoryNode[];
    existingServices: Service[];
    currencyISOCode?: string;
    onClose: () => void;
    onAddService: (service: Service) => void;
}

const AddOtherServiceItemModal = ({
    visible,
    categories,
    existingServices,
    currencyISOCode,
    onClose,
    onAddService,
}: AddOtherServiceItemModalProps) => {
    const [form] = useForm();
    const { t } = useTranslation();

    useEffect(() => {
        if (!visible) {
            form.resetFields();
        }
    }, [visible, form]);

    const parentOptions = useMemo(() => {
        return (categories || []).map((cat) => {
            const rawValue = String(cat.value ?? cat.key ?? "");
            const id = Number(rawValue.replace("cat-", ""));
            return {
                label: String(cat.title ?? cat.label ?? ""),
                value: id,
            };
        }).filter((opt) => Number.isFinite(opt.value) && opt.value > 0);
    }, [categories]);

    const amountValidator = (fieldName: string) => ({
        validator: (_: any, value: any) => {
            if (value === undefined || value === null || value === "") return Promise.resolve();
            const num = Number(value);
            if (isNaN(num) || num < 1 || num > 9999999999.99 || !/^\d{1,10}(\.\d{1,2})?$/.test(value.toString())) {
                return Promise.reject(t("invalidField", { field: fieldName }));
            }
            return Promise.resolve();
        },
    });

    const isDuplicateNameInParent = (categoryId: number, itemName: string) => {
        const normalized = itemName.trim().toLowerCase();
        if (!normalized) return false;

        const selectedParent = (categories || []).find((cat) => {
            const rawValue = String(cat.value ?? cat.key ?? "");
            return Number(rawValue.replace("cat-", "")) === categoryId;
        });

        const existsInParentChildren = (selectedParent?.children || []).some((child) => {
            const childName = String(child.title ?? child.label ?? "").trim().toLowerCase();
            return childName === normalized;
        });

        const existsInAddedServices = (existingServices || []).some((service) => {
            const sameParent = Number(service.serviceCategoryId) === Number(categoryId);
            if (!sameParent) return false;
            const serviceName = String(service.serviceCategoryItemName ?? service.serviceName ?? "").trim().toLowerCase();
            return serviceName === normalized;
        });

        return existsInParentChildren || existsInAddedServices;
    };

    const handleOk = async () => {
        const values = await form.validateFields();
        const selectedCategory = parentOptions.find((x) => x.value === values.serviceCategoryId);
        const serviceCategoryItemName = values.serviceCategoryItemName.trim().replace(/\s+/g, " ");

        onAddService({
            serviceCategoryItemId: 0,
            serviceCategoryItemName,
            serviceName: serviceCategoryItemName,
            serviceCategoryId: values.serviceCategoryId,
            serviceCategoryName: selectedCategory?.label,
            amount: values.amount,
            coveredAmount: null,
            coShare: null,
            isAmountCoverage: false,
            isServiceCovered: false,
            selected: { label: serviceCategoryItemName },
        });

        form.resetFields();
        onClose();
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={t("Add Other Sub Service")}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    {t("common.cancel", { defaultValue: t("Cancel") })}
                </Button>,
                <Button key="add" type="primary" onClick={handleOk}>
                    {t("common.add", { defaultValue: t("Add") })}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="serviceCategoryId"
                    label={t("Main Service")}
                    rules={[{ required: true, message: t("Please select a main service") }]}
                >
                    <Select
                        showSearch
                        placeholder={t("Select main service")}
                        options={parentOptions}
                        optionFilterProp="label"
                    />
                </Form.Item>

                <Form.Item
                    name="serviceCategoryItemName"
                    label={t("Sub Service")}
                    dependencies={["serviceCategoryId"]}
                    rules={[
                        { required: true, message: t("Please enter sub service") },
                        {
                            validator: (_, value) => {
                                const categoryId = form.getFieldValue("serviceCategoryId");
                                const trimmed = String(value ?? "").trim().replace(/\s+/g, " ");
                                if (!trimmed) {
                                    return Promise.reject(t("Please enter sub service"));
                                }
                                if (!categoryId) {
                                    return Promise.resolve();
                                }
                                if (isDuplicateNameInParent(Number(categoryId), trimmed)) {
                                    return Promise.reject(t("Sub service already exists in this main service"));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input placeholder={t("Enter sub service")} maxLength={100} />
                </Form.Item>

                <Form.Item
                    name="amount"
                    label={t("Amount")}
                    validateFirst={true}
                    rules={[
                        { required: true, message: t("Please enter an amount") },
                        amountValidator("Amount"),
                        currencyAmountValidator(currencyISOCode, ""),
                    ]}
                >
                    <InputNumber style={{ width: "100%" }} placeholder={t("Enter amount")} min={0} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddOtherServiceItemModal;